<template>
  <div class="p-4 border rounded">
    <div class="flex justify-between items-center mb-4">
      <h3>Assign Dimensions</h3>
      <Button
        label="Assign Dimensions"
        icon="pi pi-link"
        @click="openAssignDimensionsDialog"
      />
    </div>

    <div
      v-if="
        currentAssessmentSchema &&
        currentAssessmentSchema.dimensions &&
        currentAssessmentSchema.dimensions.length > 0
      "
    >
      <div class="grid grid-cols-3 gap-4">
        <div
          v-for="dimension in currentAssessmentSchema.dimensions"
          :key="dimension.value"
          class="p-3 border rounded-lg bg-white shadow-sm"
          :class="{
            'border-red-200 bg-red-50': !hasDimensionQuestions(dimension.value)
          }"
        >
                      <div class="flex justify-between items-start mb-2">
              <div>
                <div class="flex items-center gap-2">
                  <h4>{{ dimension?.translation?.en || '' }}</h4>
                  <span
                    v-if="!hasDimensionQuestions(dimension.value)"
                    class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full"
                    title="No questions assigned to this dimension"
                  >
                    No Questions
                  </span>
                </div>
                <p class="text-sm text-gray-600">{{ dimension?.translation?.de || '' }}</p>
              </div>
            <Button
              icon="pi pi-unlink"
              class="p-button-text p-button-danger"
              @click="unassignDimension(dimension.value)"
              v-tooltip="'Remove dimension'"
            />
          </div>
          <div class="text-sm">
            <p class="mb-2">
              <span class="font-medium">Description:</span>
              <span class="block text-gray-600">{{ dimension?.description?.en || '' }}</span>
              <span class="block text-gray-600">{{ dimension?.description?.de || '' }}</span>
            </p>
            <p>
              <span class="font-medium">Questions:</span>
              {{ getDimensionQuestionCount(dimension.value) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-4 text-gray-500">No dimensions assigned yet</div>

    <!-- Assign Dimensions Dialog -->
    <Dialog
      v-model:visible="assignDimensionsDialogVisible"
      header="Assign Dimensions to Assessment"
      :modal="true"
      class="p-fluid"
      style="width: 80vw; max-width: 800px"
    >
      <div class="space-y-4">
        <!-- Search Bar -->
        <div>
          <span class="p-input-icon-left w-full">
            <i class="pi pi-search" />
            <InputText v-model="searchTerm" placeholder="Search dimensions..." class="w-full" />
          </span>
        </div>

        <!-- Dimensions List -->
        <div class="max-h-96 overflow-y-auto">
          <div
            v-for="dimension in filteredAvailableDimensions"
            :key="dimension._id"
            class="p-3 border rounded-lg mb-2 transition-colors cursor-pointer"
            :class="{
              'bg-blue-50 border-blue-200': isDimensionAssigned(dimension.value),
              'bg-white border-gray-200 hover:bg-gray-50': !isDimensionAssigned(dimension.value),
            }"
            @click="toggleDimensionAssignment(dimension)"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <h4>{{ dimension.translation?.en || '' }}</h4>
                  <span
                    v-if="isDimensionAssigned(dimension.value)"
                    class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                  >
                    Assigned
                  </span>
                </div>
                <p class="text-sm text-gray-600">{{ dimension.translation?.de || '' }}</p>
                <p class="text-sm text-gray-500 mt-1">{{ dimension.description?.en || '' }}</p>
              </div>
              <div class="flex items-center gap-2">
                <i
                  :class="{
                    'pi pi-check-circle text-blue-600': isDimensionAssigned(dimension.value),
                    'pi pi-circle text-gray-400': !isDimensionAssigned(dimension.value),
                  }"
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filteredAvailableDimensions.length === 0" class="text-center py-4 text-gray-500">
          {{
            searchTerm ? 'No dimensions found matching your search.' : 'No dimensions available.'
          }}
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeAssignDimensionsDialog"
        />
        <Button
          label="Save Assignments"
          icon="pi pi-check"
          @click="saveDimensionAssignments"
          :loading="saving"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'DimensionsManager',
  components: {
    Button,
    InputText,
    Dialog,
  },
  computed: {
    ...mapState('assessmentSchemas', ['currentAssessmentSchema']),
    ...mapState('dimensions', {
      allDimensions: (state) => state.dimensions,
      dimensionsLoading: (state) => state.loading,
      dimensionsError: (state) => state.error,
    }),
    ...mapGetters('dimensions', ['searchDimensions']),

    // Add computed property to check if dimensions are read-only
    // Dimensions are now always configurable, including for external evaluation schemas
    isReadOnly() {
      return false;
    },

    filteredAvailableDimensions() {
      if (!this.searchTerm) {
        return this.allDimensions;
      }
      return this.searchDimensions(this.searchTerm);
    },
  },
  emits: ['dimensions-updated'],
  data() {
    return {
      assignDimensionsDialogVisible: false,
      searchTerm: '',
      saving: false,
      pendingAssignments: new Set(), // Track pending assignments
    };
  },
  mounted() {
    // Fetch all available dimensions when component mounts
    this.fetchDimensions();
  },
  methods: {
    ...mapActions('dimensions', ['fetchDimensions']),

    openAssignDimensionsDialog() {
      this.assignDimensionsDialogVisible = true;
      this.searchTerm = '';
      this.pendingAssignments.clear();

      // Initialize pending assignments with currently assigned dimensions
      const dimensions = this.currentAssessmentSchema?.dimensions;
      if (dimensions && Array.isArray(dimensions)) {
        dimensions.forEach((dim) => {
          this.pendingAssignments.add(dim.value);
        });
      }
    },

    closeAssignDimensionsDialog() {
      this.assignDimensionsDialogVisible = false;
      this.searchTerm = '';
      this.pendingAssignments.clear();
    },

    isDimensionAssigned(dimensionValue) {
      return this.pendingAssignments.has(dimensionValue);
    },

    toggleDimensionAssignment(dimension) {
      if (this.pendingAssignments.has(dimension.value)) {
        this.pendingAssignments.delete(dimension.value);
      } else {
        this.pendingAssignments.add(dimension.value);
      }
    },

    async saveDimensionAssignments() {
      this.saving = true;

      try {
        // Get the full dimension objects for assigned dimensions
        const assignedDimensions = this.allDimensions
          .filter((dim) => this.pendingAssignments.has(dim.value));

        // Update the schema with new dimension assignments
        await this.$store.dispatch('assessmentSchemas/updateSchemaDimensions', {
          schemaId: this.currentAssessmentSchema._id,
          dimensions: assignedDimensions,
        });

        this.closeAssignDimensionsDialog();
        this.$emit('dimensions-updated');

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Dimensions assigned successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to assign dimensions',
          life: 3000,
        });
      } finally {
        this.saving = false;
      }
    },

    async unassignDimension(dimensionValue) {
      try {
        await this.$store.dispatch('assessmentSchemas/removeSchemaDimension', {
          schemaId: this.currentAssessmentSchema._id,
          dimensionValue,
        });
        this.$emit('dimensions-updated');

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Dimension unassigned successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to unassign dimension',
          life: 3000,
        });
      }
    },

    getDimensionQuestionCount(dimensionValue) {
      if (!this.currentAssessmentSchema?.assessmentQuestionArray) return 0;

      return this.currentAssessmentSchema.assessmentQuestionArray.filter(
        (question) => question.properties?.dimensionName === dimensionValue,
      ).length;
    },

    hasDimensionQuestions(dimensionValue) {
      return this.getDimensionQuestionCount(dimensionValue) > 0;
    },
  },
};
</script>
